/* =============================================================================
   ics-jobstatus - the job / process status component of the own library: the
   progress bar, the page-wide job status overlay, section (per-element) jobs,
   the SignalR progress feed and the busy action button, in ONE module.

   The page-level API is shaped around a JOB handle (begin -> update -> end)
   rather than around show / hide, so a job's status and the control that fired
   it are one object.

   1. THE BAR - `class Jobstatus` on `<div data-ics="jobstatus">`.
      A pure display widget: a bordered track, a proportional fill, the caption
      rendered TWICE (base layer + inside the fill) so it stays readable in both
      tones as the fill sweeps across it.
        - the value lives in the instance; getValue() is the last CLAMPED value;
        - setValue clamps to [0,100]; onChange(new, old) with this = host fires
          on a real change (loose compare) + a bubbling 'ics:change'
          {value, oldValue}; the first render never fires it;
        - `text` is a template whose FIRST {value} is replaced; rendered through
          the Sanitizer (the SignalR feed carries user-typed master-data names);
        - re-initialising MERGES options and re-renders (the {value:0} reset
          idiom); a `text` change re-renders at the current value;
        - no animation: the fill jumps;
        - the host's content is DESTROYED on init - mount it on an empty div;
        - `aria-valuenow` is kept on the host; destroy() empties the host again.
      In-place job on the bar: start([text]) reveals it (drops the `hidden`
      attribute) at 0 and RAMPS it (+0..9 per rampMs while below rampCap) on a
      component-tracked interval; update(pct[, text]) stops the ramp at a real
      value; done() completes to 100, then hides + resets after a beat;
      isBusy() = is the ramp running. Idempotent; a destroyed bar clears its
      timers with its EventBinder.
      API: options {value, text, onChange} - nothing else is read; setValue ·
      getValue · start · update · done · isBusy. Registered as 'Jobstatus'
      ($.fn.icsJobstatus) with claimName 'progressbar' (claim-if-free: a
      receiver this component does not own is passed to the handler installed
      before it, untouched).

   2. THE PAGE JOB STATUS - ONE overlay per page, realm-shared
      (globalThis[Symbol.for('ics.jobstatus')]) so every bundle copy drives the
      same splash. It adopts the page's markup when present
      (#icsprogressbarParent > label + #icsprogressbar; the label's authored
      text becomes the default label) and builds its own (role="status",
      aria-live="polite", default label 'progress.pleaseWait') when the page
      has none.
        begin(opts)          show; `{message, percent}` is HONOURED (a real
                             percentage paints as such - no ramp); without a
                             percent the indeterminate RAMP runs (+0..9 per
                             250 ms below 90; an animation, not a measurement);
                             ONE ramp timer max; a 5-min failsafe, armed with
                             the ramp, hides an unbalanced begin with a console
                             warning. Returns the job handle {update, end}.
        update(message, pct) label + value; EITHER argument order is accepted
                             when the percent is a number (a numeric STRING is
                             read as the percent only once a message has been
                             seen; an object {message, percent} is read as
                             such); a real percent stops the ramp. Re-shows a
                             hidden overlay.
        end()                value 100, hide, then reset (value 0, default
                             label) so the next begin never flashes a stale
                             state. Calling end() twice, or without a begin, is
                             harmless.
        show() / hide()      reset to 0 + show WITHOUT a ramp; stop the ramp,
                             reset, hide. hide() adopts the page host even when
                             nothing has shown yet, so a page may call it as
                             the first statement of its ready block.
        bar()                the bar instance the overlay drives.
      SECTION JOBS - a job status over ONE element (a dashboard tile, a report
      panel, a grid host) instead of the whole page: the same overlay + bar,
      sized to the host, translucent so the stale content stays readable under
      it. ONE overlay per host, kept on the element; `section(host[, message |
      {message, percent}])` shows it (ramping unless a real percent is given)
      and returns {update, end}; `endSection(host)` removes it. A host with
      static position is made relative for the duration and restored on end().
      `host` = selector | element | jQuery set; an unresolvable host returns an
      inert handle.
      THE SERVER FEED - InitializeProgressBar() opens (or REUSES) the SignalR
      /progressHub connection and routes 'broadcastMessage'(message, progress)
      into update(); StopSignalr() closes it. Requires `globalThis.signalR`
      (HubConnectionBuilder) and `globalThis._appBasePath` on the page - either
      missing logs a console error and returns null. The hub URL is
      `_appBasePath` with trailing slashes stripped + '/progressHub', so either
      spelling of the base path works. The connection is also published as
      `globalThis._icsProgressConnection`. A disconnected connection is
      restarted on the next call, never rebuilt (rebuilding would stack
      handlers).

   3. THE ACTION - the busy action BUTTON: one module owns a job - its STATUS
      and the CONTROL that fired it. A button that fires a write (Save /
      Approve / Receive / Post / Generate) holds a BUSY state for the life of
      that request: it says "Processing...", it looks inert, it refuses a
      second press - and it goes back EXACTLY as authored when the work ends,
      on every exit.
        action(target, fnWork[, opts])  refuse a second press, go busy, run the
                                        work, restore on EVERY exit. fnWork gets
                                        (`done`, `job`): `done` is idempotent -
                                        wire it to ajax `complete:` AND call it
                                        on an early return; a synchronous throw
                                        restores and re-throws. opts: a string =
                                        the busy caption, or {text, status}.
                                        `status` is OPT-IN: true = the page job
                                        status runs for the life of the action,
                                        a selector / element / jQuery set = a
                                        SECTION job on that host, a {message,
                                        percent[, host]} object = either of
                                        those with a label. Default: the button
                                        alone. Returns false when fnWork is not
                                        a function or the set is already busy
                                        (or empty).
        busy(target[, text])            enter the state (false when any element
                                        of the set already was - the gate - or
                                        the set is empty).
        idle(target)                    leave it and restore as authored (false
                                        when nothing was busy; safe on detached
                                        elements).
        isBusy(target)                  is ANY button of the set mid-action? An
                                        absent element is never busy.
      RULES THE SERVICE ENFORCES: (a) THE GATE IS THE FLAG, NEVER THE DISABLED
      STATE - most action buttons are ANCHORS, `el.disabled` is a mere expando
      on an <a>, `:disabled` never matches one, and the Button component's
      disable() leaves jQuery handlers bound, so icsButton('disable') does NOT
      stop a double post; (b) THE RESTORE BELONGS ON EVERY EXIT - a service
      refusal arrives as an HTTP 200 carrying ErrorMessage, so the restore
      rides `complete:`, never `success:`; (c) THE CAPTION IS PER FAMILY - an
      ics button's label lives in a generated `.ics-btn__text` span (a .text()
      wipes the glyph), an <input type=button> carries it in `value`. Families
      resolved PER ELEMENT: ics `[data-ics="button"]` (the AUTHORED attribute,
      never the mount-time class) · button · input · anchor (the `disabled`
      class + href/tabindex neutralised so Enter cannot navigate; `aria-busy`
      and `aria-disabled` set). THE WHOLE SET IS ADDRESSED: '#btnSave,#btnSaveGRN'
      busies BOTH and a press on EITHER is refused. The ELEMENTS are held, not
      the selector, so a toolbar re-rendered by the work is still restored. A
      `done` that is never called leaves the button busy ON PURPOSE - no
      release timer would re-open the double-post window exactly when the
      request is slowest. (The bar's own `isBusy()` is a different receiver and
      a different question - "is my ramp running".)

   PAGE-GLOBAL SURFACE - `JOBSTATUS_GLOBALS`, installed by
   attachJobstatusGlobals(g): ProgressBarShow · ProgressBarHide ·
   ProgressBarSetValue · ProgressBarGetValue · UpdateProgressBar(a, b) (either
   order) · UpdatePickerProgress (one ramp tick) · StartPickerProgress ·
   StopPickerProgress · InitializeProgressBar · StopSignalr ·
   icsTrackPickerTimer(id) (pass-through: returns the id) · icsClearPickerTimers
   (stops the ramp) · IcsBusyAction / IcsButtonBusy / IcsButtonIdle /
   IcsButtonIsBusy (aliases of action / busy / idle / isBusy).
   `$.icsProgressBar(isStart[, {message, percent}])` (attachJobstatusFamily):
   true = begin(payload), false = end().
   `jobstatus` = {begin, update, end, show, hide, bar, section, endSection,
   settings (JOBSTATUS_SETTINGS {rampMs 250, rampCap 90, failsafeMs 5 min}),
   InitializeProgressBar, StopSignalr, action, busy, idle, isBusy} - the bundle
   entry publishes it as `Ics.jobstatus`.
   Classes this module emits: ics-jobstatus / __text / __value on the bar;
   ics-jobstatus-overlay (+ --own, --section, __box) on overlays; ics-busy on a
   busy button.

   Depends on: core/component, core/dom (create), core/options (coerce,
   parseDataOptions), services/registry, services/sanitizer,
   i18n/l10n/localisation-map (t). Paint: lib-v2/css/components/ics-jobstatus.css.
   ============================================================================= */
import { Component } from '../core/component.js';
import { Registry } from '../services/registry.js';
import { coerce, parseDataOptions } from '../core/options.js';
import { Sanitizer } from '../services/sanitizer.js';
import { create } from '../core/dom.js';
import { t } from '../i18n/l10n/localisation-map.js';

/* =============================================================================
   1. THE BAR
   ============================================================================= */
export class Jobstatus extends Component {
  static defaults = { value: 0, text: '{value}%', onChange: null };

  static resolveOptions(el, options) {
    const defaults = this.defaults;
    const data = {};
    if (el && el.dataset) {
      for (const key of Object.keys(defaults)) if (key in el.dataset) data[key] = coerce(el.dataset[key]);
      if (el.dataset.options) Object.assign(data, parseDataOptions(el.dataset.options));
    }
    return Object.assign({}, defaults, data, options || {});
  }

  _init() {
    const el = this.el;
    el.classList.add('ics-jobstatus');
    el.replaceChildren();                                 // host content is destroyed; mount on an empty div
    this._textA = create('div', { class: 'ics-jobstatus__text' });
    this._fill = create('div', { class: 'ics-jobstatus__value' });
    this._textB = create('div', { class: 'ics-jobstatus__text' });
    this._fill.appendChild(this._textB);
    el.append(this._textA, this._fill);
    if (typeof ResizeObserver !== 'undefined') {          // keeps the inner text layer sized to the bar
      const ro = this._events.observe(new ResizeObserver(() => this._syncWidth()));
      ro.observe(el);
    }
    this._value = clamp(this.options.value);              // the first render never fires onChange
    this._render();
  }

  _syncWidth() {
    const w = this.el.clientWidth;
    if (w) this._textB.style.width = w + 'px';            // inner layer spans the FULL bar (the two-tone clip)
  }

  _render() {
    const v = this._value;
    this._fill.style.width = v + '%';                     // instant, no transition
    const tpl = String(this.options.text == null ? '{value}%' : this.options.text);
    const label = tpl.replace(/{value}/, v);              // FIRST occurrence only
    Sanitizer.setContent(this._textA, label);             // safe by default: the SignalR feed carries typed names
    Sanitizer.setContent(this._textB, label);
    this.el.setAttribute('aria-valuenow', String(v));
    this._syncWidth();
  }

  setValue(v) {
    v = clamp(v);
    const old = this._value;
    this._value = v;                                      // stored CLAMPED - getValue returns this, not the DOM
    this._render();
    // eslint-disable-next-line eqeqeq
    if (old != v) {                                       // loose != : '50' and 50 are one value
      this.el.dispatchEvent(new CustomEvent('ics:change', { detail: { value: v, oldValue: old }, bubbles: true }));
      if (typeof this.options.onChange === 'function') this.options.onChange.call(this.el, v, old);
    }
    return this;
  }

  getValue() { return this._value; }

  /* ---- an IN-PLACE job on this bar (an in-window bar that reports its own work) ----
     start([text]) reveals the bar (drops a `hidden` attribute) at 0 and RAMPS it like the page overlay
     (0-9 per rampMs, capped) on a component-tracked interval - a view never writes its own ramp;
     update(pct[, text]) stops the ramp at a real value; done() completes to 100, then hides + resets after a
     beat. Idempotent; a destroyed bar clears the timer with its EventBinder. */
  start(text) {
    if (text != null) this.options.text = String(text);
    this.el.removeAttribute('hidden');
    this.setValue(0);
    if (this._busy == null) this._busy = this._events.interval(() => {
      if (this._value < JOBSTATUS_SETTINGS.rampCap) this.setValue(this._value + Math.floor(Math.random() * 10));
    }, JOBSTATUS_SETTINGS.rampMs);
    return this;
  }
  update(pct, text) {
    this._stopBusy();
    if (text != null) this.options.text = String(text);
    this.el.removeAttribute('hidden');
    return this.setValue(pct);
  }
  done() {
    this._stopBusy();
    this.setValue(100);
    if (this._doneTimer != null) this._events.clearTimer(this._doneTimer);
    this._doneTimer = this._events.timeout(() => { this._doneTimer = null; this.el.setAttribute('hidden', ''); this.setValue(0); }, 400);
    return this;
  }
  isBusy() { return this._busy != null; }
  _stopBusy() { if (this._busy != null) { this._events.clearTimer(this._busy); this._busy = null; } }

  _optionChanged(name, value) {
    if (name === 'value') { this.setValue(value); return; }     // the {value:0} reset idiom
    if (name === 'text') this._render();                        // template swap re-renders at the current value
  }

  _destroy() {
    this.el.replaceChildren();
    this.el.classList.remove('ics-jobstatus');
    this.el.removeAttribute('aria-valuenow');
  }
}

function clamp(v) {
  v = Number(v) || 0;
  if (v < 0) v = 0; if (v > 100) v = 100;                 // the ONLY guard keeping an overshooting loop <= 100
  return v;
}

Registry.add('Jobstatus', Jobstatus, {
  selector: '[data-ics="jobstatus"]',
  claimName: 'progressbar',                              // claim-if-free; when the name is already taken the bundle entry installs a dispatcher
});

/* =============================================================================
   2. THE PAGE JOB STATUS (one per page, every bundle copy shares it)
   ============================================================================= */
export const JOBSTATUS_SETTINGS = { rampMs: 250, rampCap: 90, failsafeMs: 5 * 60 * 1000 };

const SHARED_KEY = Symbol.for('ics.jobstatus');
function shared() {
  const g = globalThis;
  if (!g[SHARED_KEY]) g[SHARED_KEY] = { host: null, label: null, bar: null, own: false, ramp: null, failsafe: null, connection: null, defaultLabel: null };
  return g[SHARED_KEY];
}

const SHELL_HOST = 'icsprogressbarParent';
const SHELL_BAR = 'icsprogressbar';

/** the overlay's DOM: adopt the shell's, or build our own once */
function ensureHost() {
  const s = shared();
  if (s.host && s.host.isConnected) return s;
  if (typeof document === 'undefined') return s;
  let host = document.getElementById(SHELL_HOST);
  let bar = document.getElementById(SHELL_BAR);
  if (host) {
    host.classList.add('ics-jobstatus-overlay');
    s.own = false;
    if (!bar) { bar = create('div', { id: SHELL_BAR }); (host.firstElementChild || host).appendChild(bar); }
    s.label = host.querySelector('label');
    if (!s.label) { s.label = create('label'); (host.firstElementChild || host).insertBefore(s.label, bar); }
  } else {
    host = create('div', { id: SHELL_HOST, class: 'ics-jobstatus-overlay ics-jobstatus-overlay--own', role: 'status', 'aria-live': 'polite' });
    const box = create('div', { class: 'ics-jobstatus-overlay__box' });
    s.label = create('label');
    bar = create('div', { id: SHELL_BAR });
    box.append(s.label, bar);
    host.appendChild(box);
    host.style.display = 'none';
    document.body.appendChild(host);
    s.own = true;
  }
  if (!s.defaultLabel) s.defaultLabel = (s.label.textContent || '').trim() || t('progress.pleaseWait');
  bar.setAttribute('data-ics', 'jobstatus');
  s.bar = Component.get(bar) instanceof Jobstatus ? Component.get(bar) : new Jobstatus(bar, { value: 0 });
  s.host = host;
  return s;
}

function setLabel(msg) {
  const s = ensureHost();
  if (!s.label) return;
  Sanitizer.setContent(s.label, msg == null || msg === '' ? s.defaultLabel : String(msg));
}
function setPercent(p) {
  const s = ensureHost();
  if (s.bar) s.bar.setValue(p);
}
function isVisible() { const s = shared(); return !!(s.host && s.host.isConnected && s.host.style.display !== 'none'); }

/* ---- the ramp (an ANIMATION, never a measurement) ------------------------------- */
function rampTick() {
  const s = ensureHost();
  if (!s.bar) return;
  const v = s.bar.getValue();
  if (v < JOBSTATUS_SETTINGS.rampCap) s.bar.setValue(v + Math.floor(Math.random() * 10));   // 0-9; can land 90..98
}
function startRamp() {
  const s = shared();
  if (s.ramp !== null) return;                            // ONE timer max
  s.ramp = setInterval(rampTick, JOBSTATUS_SETTINGS.rampMs);
  if (JOBSTATUS_SETTINGS.failsafeMs > 0 && s.failsafe === null) {
    s.failsafe = setTimeout(() => {
      console.warn('[ics] jobstatus: overlay auto-hidden after ' + Math.round(JOBSTATUS_SETTINGS.failsafeMs / 60000)
        + ' min - an unbalanced begin()/$.icsProgressBar(true)');
      end();
    }, JOBSTATUS_SETTINGS.failsafeMs);
  }
}
function stopRamp() {
  const s = shared();
  if (s.ramp !== null) { clearInterval(s.ramp); s.ramp = null; }
  if (s.failsafe !== null) { clearTimeout(s.failsafe); s.failsafe = null; }
}

/** (message, percent) in EITHER order; returns {message, percent} with undefineds where absent */
function readArgs(a, b) {
  let message, percent;
  for (const x of [a, b]) {
    if (x == null || x === '') continue;
    if (typeof x === 'number' || (typeof x === 'string' && x.trim() !== '' && !isNaN(Number(x)) && message !== undefined)) { if (percent === undefined) percent = Number(x); else if (message === undefined) message = String(x); }
    else if (typeof x === 'string') { if (message === undefined) message = x; else if (percent === undefined && !isNaN(Number(x))) percent = Number(x); }
    else if (typeof x === 'object') { if (x.message != null) message = String(x.message); if (x.percent != null) percent = Number(x.percent); }
  }
  if (percent !== undefined && isNaN(percent)) percent = 0;
  return { message, percent };
}

/* ---- the page API ------------------------------------------------------------------ */
export function show() {                                  // ProgressBarShow semantics: reset to 0 + show, NO ramp
  const s = ensureHost();
  if (!s.host) return handle();
  setPercent(0);
  s.host.style.display = '';
  return handle();
}
export function hide() {                                  // ProgressBarHide semantics: stop the ramp, reset, hide
  /* ensureHost, NOT shared(): a page may call ProgressBarHide() as the FIRST statement of its ready block,
     before any show - the page host must be adopted (and hidden) right there, or the sheet's default
     display:flex leaves the splash over the whole page */
  const s = ensureHost();
  stopRamp();
  if (s.bar) s.bar.setValue(0);
  if (s.host) s.host.style.display = 'none';
  setLabel(null);
}
export function begin(opts, extra) {
  const { message, percent } = readArgs(opts, extra);
  const s = ensureHost();
  if (!s.host) return handle();
  if (s.host.style.display === 'none' || !isVisible()) { s.host.style.display = ''; if (percent === undefined) setPercent(0); }
  if (message !== undefined) setLabel(message);
  if (percent !== undefined) { stopRamp(); setPercent(percent); }
  else startRamp();
  return handle();
}
export function update(a, b) {
  const { message, percent } = readArgs(a, b);
  const s = ensureHost();
  if (!s.host) return handle();
  if (s.host.style.display === 'none') s.host.style.display = '';
  if (message !== undefined) setLabel(message);
  if (percent !== undefined) { stopRamp(); setPercent(percent); }
  return handle();
}
export function end() {
  const s = ensureHost();                                 // an end() with no begin() must still hide the page host
  if (s.bar) s.bar.setValue(100);                         // set 100 THEN hide
  hide();
}
function handle() { return { update, end }; }

/* ---- SECTION jobs - the one wait indicator for a part of a page (no per-element spinners) -------------
   A job status over ONE element (a dashboard tile, a report panel, a grid host) instead of the whole page:
   the same overlay + bar, sized to the host, translucent so the stale content stays readable under it. ONE
   overlay per host, kept on the element; `section(host[, message | {message, percent}])` shows it (ramping
   unless a real percent is given) and returns {update, end}; end() removes it. A host with static position
   is made relative for the duration and restored on end(). */
const SECTION_KEY = '__icsJobstatusSection';
function resolveEl(host) {
  if (!host) return null;
  if (typeof host === 'string') return document.querySelector(host);
  if (host.nodeType === 1) return host;
  if (host.jquery || (typeof host.length === 'number' && host[0])) return host[0] && host[0].nodeType === 1 ? host[0] : null;
  return null;
}
export function section(host, opts, extra) {
  const el = resolveEl(host);
  if (!el) return { update() { return this; }, end() {} };
  let s = el[SECTION_KEY];
  if (!s) {
    const overlay = create('div', { class: 'ics-jobstatus-overlay ics-jobstatus-overlay--section', role: 'status', 'aria-live': 'polite' });
    const box = create('div');
    const label = create('label');
    const barEl = create('div', { 'data-ics': 'jobstatus' });
    box.append(label, barEl);
    overlay.appendChild(box);
    const pos = typeof getComputedStyle === 'function' ? getComputedStyle(el).position : '';
    const madeRelative = pos === 'static' || pos === '';
    if (madeRelative) el.style.position = 'relative';
    el.appendChild(overlay);
    s = { overlay, label, bar: new Jobstatus(barEl, { value: 0 }), madeRelative };
    el[SECTION_KEY] = s;
  }
  const apply = (a, b) => {
    const { message, percent } = readArgs(a, b);
    Sanitizer.setContent(s.label, message == null || message === '' ? t('progress.pleaseWait') : String(message));
    if (percent !== undefined) s.bar.update(percent);
    else if (!s.bar.isBusy()) s.bar.start();
  };
  const h = {
    update(a, b) { if (el[SECTION_KEY] === s) apply(a, b); return h; },
    end() { endSection(el); },
  };
  s.overlay.style.display = '';
  apply(opts, extra);
  return h;
}
export function endSection(host) {
  const el = resolveEl(host);
  const s = el && el[SECTION_KEY];
  if (!s) return;
  s.bar._stopBusy();
  s.bar.destroy && s.bar.destroy();
  s.overlay.remove();
  if (s.madeRelative) el.style.position = '';
  delete el[SECTION_KEY];
}

/** the bar instance the overlay drives (null before the first use) */
export function bar() { return ensureHost().bar || null; }

/* =============================================================================
   3. THE ACTION - the busy action button. See the header for the contract and
   the rules each part enforces.
   ============================================================================= */
const BUSY_TEXT = 'Processing...';
const BUSY_STATE = '__icsBusyState';     // per-element, on the element itself
const jq = () => globalThis.jQuery || globalThis.$ || null;

/** selector | element | jQuery set | array -> the elements it addresses (the SET form of
    resolveEl above: a busy action commonly owns two buttons - a toolbar Save and a footer Save) */
function elsOf(target) {
  if (!target) return [];
  if (target.nodeType === 1) return [target];
  if (target.jquery) return target.toArray();
  if (typeof target === 'string') {
    try { return Array.prototype.slice.call(document.querySelectorAll(target)); }
    catch (e) { return []; }
  }
  if (typeof target.length === 'number') return Array.prototype.slice.call(target);
  return [];
}

/* the ics test is the AUTHORED attribute, not the `ics-btn` class (added at mount) */
function familyOf(el) {
  const $ = jq();
  if ($ && $.fn && $.fn.icsButton && el.matches && el.matches('[data-ics="button"]')) return 'ics';
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'BUTTON') return 'button';
  if (tag === 'INPUT') return 'input';
  return 'anchor';
}

function readCaption(el, family) {
  if (family === 'ics') { const o = jq()(el).icsButton('options'); return o ? o.text : ''; }
  if (family === 'input') return el.value;
  return el.innerHTML;                 // stash MARKUP: the restore must be byte-exact
}

/** write a plain-text caption - never innerHTML, the text is not markup */
function writeCaption(el, family, sText) {
  if (family === 'ics') { jq()(el).icsButton({ text: sText }); return; }
  if (family === 'input') { el.value = sText; return; }
  el.textContent = sText;
}

function restoreCaption(el, family, sCaption) {
  if (family === 'ics') { jq()(el).icsButton({ text: sCaption }); return; }
  if (family === 'input') { el.value = sCaption; return; }
  el.innerHTML = sCaption;
}

function dim(el, family, o) {
  el.classList.add('ics-busy');
  el.setAttribute('aria-busy', 'true');
  if (family === 'ics') { jq()(el).icsButton('disable'); return; }
  if (family === 'button' || family === 'input') { el.disabled = true; return; }
  el.classList.add('disabled');
  o.href = el.getAttribute('href');
  o.tabindex = el.getAttribute('tabindex');
  if (o.href != null) el.setAttribute('href', 'javascript:;');
  el.setAttribute('tabindex', '-1');
  el.setAttribute('aria-disabled', 'true');
}

function undim(el, family, o) {
  el.classList.remove('ics-busy');
  el.removeAttribute('aria-busy');
  if (family === 'ics') { jq()(el).icsButton('enable'); return; }
  if (family === 'button' || family === 'input') { el.disabled = false; return; }
  el.classList.remove('disabled');
  if (o.href != null) el.setAttribute('href', o.href);
  if (o.tabindex == null) el.removeAttribute('tabindex');
  else el.setAttribute('tabindex', o.tabindex);
  el.removeAttribute('aria-disabled');
}

/** is ANY button of this set mid-action? An absent element is never busy. */
export function isBusy(target) {
  const els = elsOf(target);
  for (let i = 0; i < els.length; i++) if (els[i][BUSY_STATE]) return true;
  return false;
}

/** enter the busy state for EVERY element of the set; false when any already was (the
    double-press refusal) or the set is empty */
export function busy(target, sText) {
  const els = elsOf(target);
  if (!els.length) return false;
  if (isBusy(els)) return false;                    // THE GATE
  const sBusy = sText || BUSY_TEXT;
  for (let i = 0; i < els.length; i++) {
    const el = els[i], family = familyOf(el);
    const o = { family, caption: readCaption(el, family), href: null, tabindex: null };
    el[BUSY_STATE] = o;
    dim(el, family, o);
    writeCaption(el, family, sBusy);
  }
  return true;
}

/** leave the busy state and restore every button of the set as authored; safe when not
    busy (false) and on an element that has since been detached */
export function idle(target) {
  const els = elsOf(target);
  let bAny = false;
  for (let i = 0; i < els.length; i++) {
    const el = els[i], o = el[BUSY_STATE];
    if (!o) continue;
    el[BUSY_STATE] = null;
    restoreCaption(el, o.family, o.caption);
    undim(el, o.family, o);
    bAny = true;
  }
  return bAny;
}

/** `opts`: a string (the busy caption) or {text, status}. `status` is OPT-IN:
    true = the PAGE job status runs for the life of the action; a selector /
    element / jQuery set = a SECTION job on that host; an object {message, percent[, host]}
    = either of those with a label. Returns the job handle so the caller can update it. */
function startStatus(status) {
  if (status == null || status === false) return null;
  if (status === true) return begin();
  if (typeof status === 'string' || status.nodeType === 1 || status.jquery) return section(status);
  if (typeof status === 'object') {
    if (status.host != null) return section(status.host, status);
    return begin(status);
  }
  return null;
}

/** the whole shape: refuse a second press, go busy, run the work, restore on EVERY exit. */
export function action(target, fnWork, opts) {
  if (typeof fnWork !== 'function') return false;
  const o = (typeof opts === 'string') ? { text: opts } : (opts || {});
  if (!busy(target, o.text)) return false;
  /* hold the ELEMENTS, not the selector: the work may replace the toolbar (a grid refresh,
     a re-rendered footer) and a re-query would then restore nothing while the old nodes
     stayed dimmed */
  const els = elsOf(target);
  const job = startStatus(o.status);
  let bDone = false;
  const done = () => { if (bDone) return; bDone = true; idle(els); if (job) { try { job.end(); } catch (e) { } } };
  try { fnWork(done, job); }
  catch (e) { done(); throw e; }                     // a synchronous throw is an exit too
  return true;
}

/* ---- the server feed --------------------------------------------------------------- */
function hubUrl() {
  const base = String(globalThis._appBasePath == null ? '' : globalThis._appBasePath).replace(/\/+$/, '');
  return base + '/progressHub';
}
export function InitializeProgressBar() {
  const s = shared();
  const sig = globalThis.signalR;
  if (s.connection) {                                     // reuse, never rebuild (rebuilding stacks handlers)
    if (sig && sig.HubConnectionState && s.connection.state === sig.HubConnectionState.Disconnected) {
      s.connection.start()
        .then(() => console.log('[ics] jobstatus: SignalR connection restarted'))
        .catch((err) => console.error('[ics] jobstatus: SignalR connection error', err));
    }
    return s.connection;
  }
  if (typeof globalThis._appBasePath === 'undefined') { console.error('[ics] jobstatus: _appBasePath is not defined - the shell must emit it'); return null; }
  if (!sig || !sig.HubConnectionBuilder) { console.error('[ics] jobstatus: signalR client not loaded'); return null; }
  const b = new sig.HubConnectionBuilder().withUrl(hubUrl()).withAutomaticReconnect();
  const conn = (b.configureLogging && sig.LogLevel ? b.configureLogging(sig.LogLevel.Information) : b).build();
  /* extra args tolerated: the same event also carries wider notifications; (message, progress) is the
     server's order, but a numeric first argument is read as the percent all the same */
  conn.on('broadcastMessage', function (message, progress) { update(message, progress); });
  conn.start()
    .then(() => console.log('[ics] jobstatus: SignalR connection started'))
    .catch((err) => console.error('[ics] jobstatus: SignalR connection error', err));
  s.connection = conn;
  globalThis._icsProgressConnection = conn;               // also published as a page global under this name
  return conn;
}
export function StopSignalr() {
  const s = shared();
  if (s.connection) s.connection.stop().then(() => console.log('[ics] jobstatus: SignalR connection stopped'));
}

/* ---- the page-global surface --------------------------------------------------------- */
export function ProgressBarShow() { show(); }
export function ProgressBarHide() { hide(); }
export function ProgressBarSetValue(nValue) { setPercent(nValue); }
/** the page bar's current (clamped) value; 0 before any bar exists */
export function ProgressBarGetValue() { const b = ensureHost().bar; return b ? b.getValue() : 0; }
export function UpdateProgressBar(a, b) { update(a, b); }
export function UpdatePickerProgress() { rampTick(); }
export function StartPickerProgress() { startRamp(); }
export function StopPickerProgress() { stopRamp(); }
export function icsTrackPickerTimer(id) { return id; }    // pass-through: the ramp timer is owned here, there is nothing to track
export function icsClearPickerTimers() { stopRamp(); }

/* the busy-action four: aliases of the layer-3 names */
export const IcsBusyAction = action;
export const IcsButtonBusy = busy;
export const IcsButtonIdle = idle;
export const IcsButtonIsBusy = isBusy;

export const JOBSTATUS_GLOBALS = {
  ProgressBarShow, ProgressBarHide, ProgressBarSetValue, ProgressBarGetValue, UpdateProgressBar,
  UpdatePickerProgress, StartPickerProgress, StopPickerProgress,
  InitializeProgressBar, StopSignalr, icsTrackPickerTimer, icsClearPickerTimers,
  IcsBusyAction, IcsButtonBusy, IcsButtonIdle, IcsButtonIsBusy,
};
export function attachJobstatusGlobals(g) {
  const target = g || globalThis;
  let n = 0;
  for (const k of Object.keys(JOBSTATUS_GLOBALS)) { target[k] = JOBSTATUS_GLOBALS[k]; n++; }
  return n;
}
/** $.icsProgressBar(isStart[, {message, percent}]) - the payload is HONOURED (a real percent paints, no ramp) */
export function attachJobstatusFamily($) {
  if (!$) return false;
  $.icsProgressBar = function (isStart, payload) {
    if (isStart) begin(payload);
    else end();
  };
  return true;
}

export const jobstatus = {
  begin, update, end, show, hide, bar, section, endSection, settings: JOBSTATUS_SETTINGS,
  InitializeProgressBar, StopSignalr,
  action, busy, idle, isBusy,                        // layer 3: the control that fired the job
};

/** test seam - drains timers/connection (never used by app code) */
export function _resetJobstatus() { stopRamp(); const s = shared(); s.connection = null; if (s.own && s.host) { s.host.remove(); } s.host = null; s.bar = null; s.label = null; s.defaultLabel = null; }
